import React, { useState, useEffect, Suspense, useRef, lazy } from 'react';
import {
  useNavigate,
  useSearchParams,
  useLocation
} from 'react-router-dom';

import { mount, unmount } from 'redom';

import ScrollShadows from 'generics/components/scroll-shadows';

import application from 'application';

import SimpleDialog from 'generics/components/simple-dialog';
import GenericSearchPanel from 'generics/components/generic-search-panel';

import StartupSelectBox from '../startup-select-box';

import StartupNavigation from './navigation';
import TagsNavigator from './tags-navigator';

import applicationEventCodes from 'application-event-codes';
import externalEventCodes from 'event-system/codes/external';
import internalEventCodes from 'event-system/codes/internal';
import NavItem from 'generics/components/nav-item';
import { ReactComponent as SettingsIcon } from 'assets/images/quick-access-setting-icon.svg';
import { ReactComponent as CloseIcon } from 'assets/images/close-button-icon.svg';
import { ReactComponent as AssistantIcon } from 'assets/images/ask-ai-medium-icon.svg';
import { ReactComponent as RestoreApplicationWidthIcon } from 'assets/images/restore-application-width-icon.svg';

import HelpWidget from './help-widget';

import { loadModule } from 'utilities/dynamic-import';

import './index.scss';

function importStartupForm() {
  return loadModule(function doImportStartupForm() {
    return import('root/startup-form');
  });
}

const StartupForm = lazy(importStartupForm);

let getSectionNameForTabTitle;

async function loadStartupFormHelpers() {
  if (!getSectionNameForTabTitle) {
    const module = await importStartupForm();
    getSectionNameForTabTitle = module.getSectionNameForTabTitle;
  }
  return getSectionNameForTabTitle;
};

function QuickAccess(props) {

  const startup = props.startup;

  const navigate = useNavigate();
  const location = useLocation();

  const userQuickAccessRef = useRef(null);
  const helpWidgetRef = useRef(null);
  const searchPanelRef = useRef(null);
  const searchPanelContainerRef = useRef(null);
  const authenticatedContentRef = useRef(null);
  const scrollShadowsRef = useRef(null);

  const [searchParameters, setSearchParameters] = useSearchParams();

  // The profile dialog is opened and closed by callbacks registered once, on mount. `setSearchParameters`
  // resolves a function it is handed against the parameters of the render that produced it, so those
  // callbacks would write the address the app was loaded with and take every other dialog's parameter
  // off the URL with them. They read the parameters as they are now instead, and hand over a set rather
  // than a function.
  const searchParametersReference = useRef(searchParameters);

  searchParametersReference.current = searchParameters;

  const [isFirstRender, setIsFirstRender] = useState(true);
  const [rootState, setRootState] = useState({
    displayAuthenticationPage: false,
    displayEditStartupDialog: false,
    displaySpinnerDialog: false,
    activeStartupFormSection: 'ABOUT',
    loginButtonText: determineLoginButtonText(),
    displayUserProfileEditDialog: false,
    assistantOptions: null,
    startups: application.getAllAssociatedStartups(),
    displayAssistantUnreadMessageCue: false,
    displaySearchPanel: false,
    onboarding: {
      steps: { total: 0, completed: 0 },
      displayLink: false
    }
  });

  const [user, setUser] = useState(application.getUser());

  useEffect(function onLoad() {

    const cleanup = initialize();

    const keyboardEventListener = registerKeyboardShortcut();

    return function cleanupOnUnmount() {
      if (cleanup !== void 0) {
        cleanup();
      }
      keyboardEventListener();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function onSearchParametersChange() {

    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }

    if (rootState.displayUserProfileEditDialog === false && searchParameters.has('dialog-name')) {
      tryShowUserProfileEditDialog();
    }

    if (searchParameters.has('help')) {
      helpWidgetRef.current?.showLiveChat();
    } else {
      helpWidgetRef.current?.hideLiveChat();
    }

    if (searchParameters.has('search')) {
      showSearchPanel();
    } else {
      hideSearchPanel();
    }

    //

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParameters]);

  useEffect(function registerEvents() {

    const eventRegistrers = [
      registerToNavigateToEvent,
      listenToUserProfileUpdatedEvent,
      registerDisplayAuthenticatorEventHandler,
      registerStartupUserFullNameGeneratedEventHandler,
      registerFacebookIntegrationExpiredEventHandler,
      registerLinkedInIntegrationExpiredEventHandler,
      registerTwitterIntegrationExpiredEventHandler,
      registerShowStartupEditorEventHandler,
      registerShowProfileDialogEventHandler,
      registerAssistantMessageCueEventHandler,
      registerToShowSearchPanelEvent,
      registerToToggleExpandedEvent,
      registerToResetExpandedEvent,
      registerToOnboardingStepCompletedEvent,
      registerToUpdateSearchParamsEvent
    ];
    const eventUnregisters = [];

    for (let eventRegistrer of eventRegistrers) {
      eventUnregisters.push(eventRegistrer());
    }

    return function unregisterEventListeners() {

      for (let eventUnregister of eventUnregisters) {
        eventUnregister();
      }

    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function onSearchParametersChange() {

    if (searchParameters.get('settings') !== null) {
      setDisplayEditStartupDialog(application.accessController.canEditStartupSettings());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParameters]);

  useEffect(function attachScrollShadows() {

    // var element = authenticatedContentRef.current;

    // if (element === null) {
    //   return;
    // }

    // var shadows = new ScrollShadows(element);
    // scrollShadowsRef.current = shadows;
    // mount(element.parentNode, shadows);

    // return function cleanupScrollShadows() {
    //   shadows.destroy();
    //   unmount(element.parentNode, shadows);
    //   scrollShadowsRef.current = null;
    // };

  }, []);

  useEffect(function onSearchParametersChange() {

    const expanded = searchParameters.get('expanded');

    if (expanded === '1') {
      application.fullscreenToggle.expand();
    } else {
      application.fullscreenToggle.restore();
    }
    document.getElementById('middle-panel-grab-handle')?.parentNode?.component.reposition();
    setLoginButtonText();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParameters]);

  function updateSearchParameters(updates) {

    const nextSearchParameters = new URLSearchParams(window.location.search);

    for (let update of updates) {

      if (update.type === 'DELETE') {
        nextSearchParameters.delete(update.key);
      } else if (update.type === 'PUT') {
        nextSearchParameters.set(update.key, update.value);
      }

    }

    navigate({
      pathname: window.location.pathname,
      search: `?${nextSearchParameters.toString()}`
    }, { replace: true });

  }

  function determineLoginButtonText() {

    const authenticationSettings = props.startup.authentication;

    let buttonText = 'Login or Signup';

    if (authenticationSettings.enabled === true) {
      buttonText = authenticationSettings.buttonText || `Login using ${props.startup.name}`;
    }

    if (application.fullscreenToggle.isExpanded() === true) {
      buttonText = 'L/S';
    }

    return buttonText;

  }

  function setLoginButtonText() {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        loginButtonText: determineLoginButtonText()
      };

    });

  }

  function toggleExpandedState() {

    const nextSearchParameters = new URLSearchParams(window.location.search);

    const expanded = nextSearchParameters.get('expanded');

    if (expanded === '1') {
      nextSearchParameters.delete('expanded');
    } else {
      nextSearchParameters.set('expanded', '1');
    }

    navigate({
      pathname: window.location.pathname,
      search: `?${nextSearchParameters.toString()}`
    }, { replace: true });

  }

  function resetExpandedState() {

    const nextSearchParameters = new URLSearchParams(window.location.search);

    const expanded = nextSearchParameters.get('expanded');

    if (expanded === '1') {
      nextSearchParameters.delete('expanded');
    }

    navigate({
      pathname: window.location.pathname,
      search: `?${nextSearchParameters.toString()}`
    }, { replace: true });

  }

  function onboardingPlanStatus(planData) {

    const total = planData.items.length;
    let completed = 0;

    for (let item of planData.items) {
      if (item.status !== 'PENDING') {
        completed = completed + 1;
      }
    }

    return {
      steps: { total, completed },
      displayLink: total > completed
    };

  }

  async function loadOnboardingLink() {

    if (application.accessController.isMemberOrAdministrator() === false) {
      return;
    }

    const planData = await application.startupModule.loadStartupPlan();
    const status = onboardingPlanStatus(planData);

    setRootState(function updateRootState(rootState) {
      return {
        ...rootState,
        onboarding: status
      };
    });

  }

  function registerToOnboardingStepCompletedEvent() {

    function handleOnboardingStepCompletedEvent() {
      setRootState(function updateRootState(rootState) {
        const completed = rootState.onboarding.steps.completed + 1;
        return {
          ...rootState,
          onboarding: {
            ...rootState.onboarding,
            steps: {
              ...rootState.onboarding.steps,
              completed
            },
            displayLink: rootState.onboarding.steps.total > completed
          }
        };
      });
    }

    application.eventSystem.internal.on('ONBOARDING_STEP_COMPLETED', handleOnboardingStepCompletedEvent);

    return function unregisterToOnboardingStepCompletedEvent() {
      application.eventSystem.internal.off('ONBOARDING_STEP_COMPLETED', handleOnboardingStepCompletedEvent);
    };

  }

  function tryRenderOnboardingLink() {

    if (rootState.onboarding.displayLink === false) {
      return;
    }

    const percent = ((rootState.onboarding.steps.completed / rootState.onboarding.steps.total) * 100).toFixed(0);

    return <NavItem to="/welcome">
      <svg width="26" height="26" viewBox="0 0 26 26" xmlns="http://www.w3.org/2000/svg">
        <circle cx="13" cy="13" r="10" fill="none" stroke="var(--startup-tertiary-color)" strokeWidth="2.5" />
        <circle cx="13" cy="13" r="10" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" transform="rotate(-90 13 13)" pathLength="100" strokeDasharray="100" strokeDashoffset={100 - percent} style={{ transition: 'stroke-dashoffset 400ms ease' }} />
        <text x="13" y="15.5" textAnchor="middle" fontSize="6" fontFamily="Arial, sans-serif" fill="currentColor">{percent}%</text>
      </svg>
    </NavItem>;

  }

  function readCurrentSearchParameters() {

    return new URLSearchParams(searchParametersReference.current);

  }

  function clearProfileDialogSearchParams() {

    setDisplayUserProfileEditDialog(false);

    const nextSearchParameters = readCurrentSearchParameters();

    nextSearchParameters.delete('dialog-name');
    nextSearchParameters.delete('section');

    setSearchParameters(nextSearchParameters);

  }

  function updateProfileDialogSearchParams(section) {

    const nextSearchParameters = readCurrentSearchParameters();

    nextSearchParameters.set('dialog-name', 'user-profile');
    nextSearchParameters.set('section', section.name);

    setSearchParameters(nextSearchParameters);

  }

  function setDisplayAuthenticationPage(displayAuthenticationPage) {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        displayAuthenticationPage
      };

    });

  }

  function setDisplayEditStartupDialog(displayEditStartupDialog) {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        displayEditStartupDialog
      };

    });

  }

  function tryShowBillingSection() {

    const owner = startup.owner;

    if (owner.invoiceOverDue === false && owner.metricLimitReached === false) {
      return false;
    }

    if (application.accessController.isMemberOrAdministrator() === false) {
      return false;
    }

    showUserProfileEditor({
      defaultActiveSectionName: 'Billing',
      disallowCancel: true
    });

    return true;

  }

  function tryShowUserProfileEditDialog() {

    const displayed = tryShowBillingSection();

    if (displayed === true) {
      return;
    }

    const dialogName = searchParameters.get('dialog-name');
    const sectionName = searchParameters.get('section');

    if (dialogName !== 'user-profile') {
      return;
    }

    showUserProfileEditor({
      defaultActiveSectionName: sectionName
    });

  }

  function preloadResources() {

    function warmStartupForm() {
      loadStartupFormHelpers();
    }

    if (window.requestIdleCallback) {
      window.requestIdleCallback(warmStartupForm);
    } else {
      window.setTimeout(warmStartupForm, 2000);
    }

  }

  function registerKeyboardShortcut() {

    function handleKeyDown(event) {

      const isCmdOrCtrl = event.metaKey || event.ctrlKey;
      const isKKey = event.key === 'k' || event.key === 'K';

      if (isCmdOrCtrl === true && isKKey === true) {
        event.preventDefault();

        const currentSearchParams = new URLSearchParams(window.location.search);

        if (currentSearchParams.has('search') === true) {
          hideSearchPanelViaQueryParameter();
        } else {
          showSearchPanelViaQueryParameter();
        }

      }

    }

    document.addEventListener('keydown', handleKeyDown);

    return function unregisterKeyboardShortcut() {
      document.removeEventListener('keydown', handleKeyDown);
    };

  }

  function showSearchPanelViaQueryParameter() {

    const currentPath = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);

    searchParams.set('search', '');

    navigate(`${currentPath}?${searchParams.toString()}`, { replace: true });

  }

  function hideSearchPanelViaQueryParameter() {

    const currentPath = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);

    searchParams.delete('search');

    navigate(`${currentPath}?${searchParams.toString()}`, { replace: true });

  }

  function showSearchPanel() {

    const outOfPlaceContainer = document.getElementById('out-of-place-content-container');

    if (outOfPlaceContainer === null) {
      return;
    }

    if (searchPanelRef.current === null) {
      searchPanelRef.current = new GenericSearchPanel();
    }

    mount(outOfPlaceContainer, searchPanelRef.current);

    const searchTerm = searchParameters.get('search') || '';

    searchPanelRef.current.show({
      searchTerm,
      onClose: hideSearchPanelViaQueryParameter
    });

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        displaySearchPanel: true
      };

    });

  }

  // Decided by whether the panel is on the page, not by the state flag: a flag that has fallen
  // out of step would otherwise leave a panel no click or key can close.
  function hideSearchPanel() {

    if (searchPanelRef.current === null) {
      return;
    }

    const searchPanelParent = searchPanelRef.current.el.parentNode;

    if (searchPanelParent === null) {
      return;
    }

    searchPanelRef.current.hide();
    unmount(searchPanelParent, searchPanelRef.current);

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        displaySearchPanel: false
      };

    });

  }

  function tryDisplayHelpWidget() {

    if (userQuickAccessRef.current === null) {
      return;
    }

    if (helpWidgetRef.current === null) {
      helpWidgetRef.current = HelpWidget.get();
    }

    if (application.accessController.isMemberOrAdministrator() === false) {
      return;
    }

    mount(userQuickAccessRef.current, helpWidgetRef.current);

  }

  function tryUnmountHelpWidget() {
    if (helpWidgetRef.current === null) {
      return;
    }

    if (userQuickAccessRef.current === null) {
      return;
    }

    try {

      unmount(userQuickAccessRef.current, helpWidgetRef.current);

    } catch (exception) {

      console.error(exception);

    }

  }

  async function loadAllStartups() {

    await application.loadAllAssociatedStartups();

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        startups: application.getAllAssociatedStartups()
      };

    });

  }

  function initialize() {

    loadAllStartups();
    tryOpenStartupSettingsEditor();
    tryShowUserProfileEditDialog();

    preloadResources();

    tryDisplayHelpWidget();

    loadOnboardingLink();

    if (searchParameters.has('search')) {
      showSearchPanel();
    }

    return function cleanup() {
      tryUnmountHelpWidget();
      hideSearchPanel();
    };

  }

  async function tryOpenStartupSettingsEditor() {

    if (searchParameters.get('account-integrated') !== 'true') {
      return;
    }

    searchParameters.delete('dialog-name');
    setSearchParameters(searchParameters);
    setDisplayEditStartupDialog(true);

  }

  function listenToUserProfileUpdatedEvent() {

    function handleUserProfileUpdatedEvent(data) {

      setUser(function updateUser(user) {

        return {
          ...user,
          ...data
        };

      });

    }

    application.eventSystem.internal.on(internalEventCodes.CURRENT_USER_PROFILE_UPDATED, handleUserProfileUpdatedEvent);

    return function unlistenUserProfileUpdatedEvent() {
      application.eventSystem.internal.off(internalEventCodes.CURRENT_USER_PROFILE_UPDATED, handleUserProfileUpdatedEvent);
    };

  }

  function registerDisplayAuthenticatorEventHandler() {

    function handleDisplayAuthenticatorEvent() {

      handleLoginButtonClick();

    }

    application.eventSystem.internal.on(internalEventCodes.DISPLAY_AUTHENTICATOR, handleDisplayAuthenticatorEvent);

    return function unregisterDisplayAuthenticatorEventHandler() {
      application.eventSystem.internal.off(internalEventCodes.DISPLAY_AUTHENTICATOR, handleDisplayAuthenticatorEvent);
    };

  }

  function registerShowProfileDialogEventHandler() {

    function handleShowProfileDialogEvent(data) {

      showUserProfileEditor(data);

    }

    application.eventSystem.internal.on(applicationEventCodes.DISPLAY_PROFILE_DIALOG, handleShowProfileDialogEvent);

    return function unregisterShowProfileDialogEventHandler() {
      application.eventSystem.internal.off(applicationEventCodes.DISPLAY_PROFILE_DIALOG, handleShowProfileDialogEvent);
    };

  }

  function registerToNavigateToEvent() {

    function handleNavigateToEvent(data) {

      navigate(data.to, { state: data.state || {} });

    }

    application.eventSystem.internal.on(internalEventCodes.NAVIGATE_TO, handleNavigateToEvent);

    return function unregisterNavigateToEventHandler() {
      application.eventSystem.internal.off(internalEventCodes.NAVIGATE_TO, handleNavigateToEvent);
    };

  }

  function registerToShowSearchPanelEvent() {

    function handleShowSearchPanelEvent() {

      showSearchPanelViaQueryParameter();

    }

    application.eventSystem.internal.on('SHOW_SEARCH_PANEL', handleShowSearchPanelEvent);

    return function unregisterNavigateToEventHandler() {
      application.eventSystem.internal.off('SHOW_SEARCH_PANEL', handleShowSearchPanelEvent);
    };

  }

  function registerToToggleExpandedEvent() {

    function handleToggleExpandedEvent() {

      toggleExpandedState();

    }

    application.eventSystem.internal.on('TOGGLE_EXPANDED', handleToggleExpandedEvent);

    return function unregisterNavigateToEventHandler() {
      application.eventSystem.internal.off('TOGGLE_EXPANDED', handleToggleExpandedEvent);
    };

  }

  function registerToResetExpandedEvent() {

    function handleResetExpandedEvent() {

      resetExpandedState();

    }

    application.eventSystem.internal.on('RESET_MIDDLE_PANEL_EXPANDED', handleResetExpandedEvent);

    return function unregisterNavigateToEventHandler() {
      application.eventSystem.internal.off('RESET_MIDDLE_PANEL_EXPANDED', handleResetExpandedEvent);
    };

  }

  function registerToUpdateSearchParamsEvent() {

    function handler(updates) {

      updateSearchParameters(updates);

    }

    application.eventSystem.internal.on('UPDATE_SEARCH_PARAMS', handler);

    return function unregisterNavigateToEventHandler() {
      application.eventSystem.internal.off('UPDATE_SEARCH_PARAMS', handler);
    };

  }

  function registerStartupUserFullNameGeneratedEventHandler() {

    function handleStartupUserFullNameGenerated(startupUser) {

      setUser(function updateUser(user) {

        if (user._id !== startupUser._id) {
          return user;
        }

        return {
          ...user,
          ...startupUser
        };

      });

    }

    application.eventSystem.internal.on(externalEventCodes.STARTUP_USER_FULL_NAME_GENERATED, handleStartupUserFullNameGenerated);

    return function unregisterStartupUserFullNameGeneratedEventHandler() {
      application.eventSystem.internal.off(externalEventCodes.STARTUP_USER_FULL_NAME_GENERATED, handleStartupUserFullNameGenerated);
    };

  }

  function registerFacebookIntegrationExpiredEventHandler() {

    function handleFacebookIntegrationExpired(startup) {

      application.setCurrentStartup(startup);

      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
        message: 'Facebook integration expired. Please re-authorize integration!',
        seconds: 5,
        type: 'error'
      });

    }

    application.eventSystem.internal.on(externalEventCodes.STARTUP_FACEBOOK_ACCOUNT_INTEGRATION_EXPIRED, handleFacebookIntegrationExpired);

    return function unregisterFacebookIntegrationExpiredEventHandler() {
      application.eventSystem.internal.off(externalEventCodes.STARTUP_FACEBOOK_ACCOUNT_INTEGRATION_EXPIRED, handleFacebookIntegrationExpired);
    };

  }

  function registerLinkedInIntegrationExpiredEventHandler() {

    function handleLinkedInIntegrationExpired(startup) {

      application.setCurrentStartup(startup);

      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
        message: 'LinkedIn integration expired. Please re-authorize integration!',
        seconds: 5,
        type: 'error'
      });

    }

    application.eventSystem.internal.on(externalEventCodes.STARTUP_LINKED_IN_ACCOUNT_INTEGRATION_EXPIRED, handleLinkedInIntegrationExpired);

    return function unregisterLinkedInIntegrationExpiredEventHandler() {
      application.eventSystem.internal.off(externalEventCodes.STARTUP_LINKED_IN_ACCOUNT_INTEGRATION_EXPIRED, handleLinkedInIntegrationExpired);
    };

  }

  function registerTwitterIntegrationExpiredEventHandler() {

    function handleTwitterIntegrationExpired(startup) {

      application.setCurrentStartup(startup);

      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
        message: 'Twitter integration expired. Please re-authorize integration!',
        seconds: 5,
        type: 'error'
      });

    }

    application.eventSystem.internal.on(externalEventCodes.STARTUP_TWITTER_ACCOUNT_INTEGRATION_EXPIRED, handleTwitterIntegrationExpired);

    return function unregisterTwitterIntegrationExpiredEventHandler() {
      application.eventSystem.internal.off(externalEventCodes.STARTUP_TWITTER_ACCOUNT_INTEGRATION_EXPIRED, handleTwitterIntegrationExpired);
    };

  }

  function registerShowStartupEditorEventHandler() {

    async function handleShowStartupEditorEvent(options = { activeStartupFormSection: 'ABOUT' }) {

      const getSectionNameForTabTitleFn = await loadStartupFormHelpers();
      const sectionName = getSectionNameForTabTitleFn(options.activeStartupFormSection);

      setSearchParameters(function updateSearchParameters(searchParameters) {

        searchParameters.set('settings', sectionName);

        return searchParameters;

      });

    }

    application.eventSystem.internal.on(internalEventCodes.SHOW_STARTUP_EDITOR, handleShowStartupEditorEvent);

    return function unregisterTwitterIntegrationExpiredEventHandler() {
      application.eventSystem.internal.off(internalEventCodes.SHOW_STARTUP_EDITOR, handleShowStartupEditorEvent);
    };

  }

  function registerAssistantMessageCueEventHandler() {

    async function handleAssistantMessageCueEvent() {

      setRootState(function updateRootState(rootState) {

        return {
          ...rootState,
          displayAssistantUnreadMessageCue: true
        };

      });

    }

    application.eventSystem.internal.on('SHOW_NEW_ASSISTANT_MESSAGE_CUE', handleAssistantMessageCueEvent);

    return function unregisterTwitterIntegrationExpiredEventHandler() {
      application.eventSystem.internal.off('SHOW_NEW_ASSISTANT_MESSAGE_CUE', handleAssistantMessageCueEvent);
    };

  }

  function hideEditStartupDialog() {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        displayEditStartupDialog: false,
        activeStartupFormSection: 'ABOUT'
      };

    });

  }

  async function handleStartupEditingCompleted() {

    hideEditStartupDialog();
    await refreshCurrentStartup();

  }

  async function refreshCurrentStartup() {

    await props.activateStartup(true);

  }

  async function handleLogout() {

    clearProfileDialogSearchParams();

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message: 'You are logged out!',
      seconds: 5,
      type: 'info'
    });

    await application.clearAuthCookies();

    window.location.href = '/community';

  }

  function openCenteredWindow(url, title, width, height) {

    const screen = window.screen;

    if (width > screen.width) {
      width = screen.width * 0.8;
    }
    if (height > screen.height) {
      height = screen.height * 0.8;
    }

    const left = (screen.width - width) / 2;
    const top = (screen.height - height) / 2;

    window.location.href = url;

  }

  function getAuthenticatorQueryString() {

    return `?origin=${window.location.host}&path=${window.location.pathname}&observerId=${application.startupUserObserver.getObserverId()}`;

  }

  function handleLoginButtonClick() {

    const authenticationSettings = props.startup.authentication;
    const queryString = getAuthenticatorQueryString();

    if (authenticationSettings.enabled === true) {
      window.location.href = authenticationSettings.url + queryString;
    } else {
      const authenticationSettings = props.startup.authentication;

      let authenticatorURL = application.getAuthenticatorURL();

      if (authenticationSettings.enabled === true) {
        authenticatorURL = authenticationSettings.url + queryString;
      }

      openCenteredWindow(authenticatorURL, 'Login/SignUp', 800, 600);
    }

  }

  function showSpinnerDialog() {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        displaySpinnerDialog: true
      };

    });

  }

  function hideSpinnerDialog() {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        displaySpinnerDialog: false
      };

    });

  }

  function setDisplayUserProfileEditDialog(displayUserProfileEditDialog) {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        displayUserProfileEditDialog
      };

    });

  }

  async function showUserProfileEditor(configuration = {}) {

    setDisplayUserProfileEditDialog(true);
    showSpinnerDialog();

    const { default: APIInterface } = (await import( /* @vite-ignore */ import.meta.env.REACT_APP_BROWSER_COMPONENTS_BASE_ADDRESS + '/modules/api-interface/index.js'));
    const { UserProfileDialogFacade } = (await import( /* @vite-ignore */ import.meta.env.REACT_APP_BROWSER_COMPONENTS_BASE_ADDRESS + '/components/application/user-profile-editor/index.js'));

    const apiInterface = new APIInterface({
      startupId: props.startup._id,
      authorizationToken: application.getAuthorizationToken()
    });

    await apiInterface.initializeUserInterface(user);

    application.dialogsManager.dialogActivated();

    const userProfileDialogFacade = new UserProfileDialogFacade({
      ...configuration,
      application,
      startup: application.getCurrentStartup(),
      userId: user._id,
      apiInterface,
      hideLogoutButton: false,
      onLogout: handleLogout,
      displayVenturzBilling: application.accessController.isAdministrator(),
      displayAvailabilitySettings: application.accessController.isMemberOrAdministrator(),
      onSectionChange(section) {
        updateProfileDialogSearchParams(section);
      }
    });

    hideSpinnerDialog();

    userProfileDialogFacade.show().then(function onClose() {
      clearProfileDialogSearchParams();
      application.dialogsManager.dialogDeactivated();
    });

    return userProfileDialogFacade;

  }

  function renderProfileSettingsLink() {

    if (application.isAuthenticated() === false) {
      return;
    }

    const user = application.getUser();

    const profileSettingsLinkAttributes = {
      onClick() {
        showUserProfileEditor();
      },
      className: 'profile-settings-link'
    };

    let profilePictureURL = import.meta.env.REACT_APP_ASSETS_URL + '/assets/images/default-profile-picture.png';

    if (user.profilePicture.url !== '') {
      profilePictureURL = user.profilePicture.url;
    }

    const currentUserPictureAttributes = {
      id: 'current-user-picture',
      className: 'profile-picture',
      style: {
        backgroundImage: `url(${profilePictureURL})`
      },
    };

    return <button {...profileSettingsLinkAttributes}>
      <span {...currentUserPictureAttributes}></span>
      <span className="startup-owner-name header ellipsis">{user.fullName || 'Site Visitor'}</span>
    </button>;

  }

  function renderAuthenticationPage() {

    if (rootState.displayAuthenticationPage === false) {
      return;
    }

    const dialogProperties = {
      stackTopClass: 'quick-access-elevated',
      contentHolderClassName: 'authenticator-container',
      onClose() {
        setDisplayAuthenticationPage(false);
      }
    };

    const authenticationSettings = props.startup.authentication;

    let authenticatorURL = application.getAuthenticatorURL();

    if (authenticationSettings.enabled === true) {
      authenticatorURL = authenticationSettings.url + getAuthenticatorQueryString();
    }

    window.location.href = authenticatorURL;

    const iframeProps = {
      src: authenticatorURL,
      width: '995px',
      className: 'authenticator-iframe'
    };

    return <SimpleDialog {...dialogProperties}>
      <div className='authenticator-iframe-container'>
        {renderIFrameHeaderSmallScreen()}
        <iframe title='Authenticator' {...iframeProps} />
      </div>
    </SimpleDialog>;

  }

  function renderIFrameHeaderSmallScreen() {

    const closeControlAttributes = {
      className: 'iframe-header-close-control',
      onClick() {
        setDisplayAuthenticationPage(false);
      }
    };

    return <div className='iframe-header'>
      <button {...closeControlAttributes}>
        <CloseIcon className='close-icon' />
      </button>
    </div>;
  }

  function renderStartupSelectBox() {

    const startupSelectBoxAttributes = {
      startups: rootState.startups,
      selected: startup,
      onChange(startup) {

        window.location.href = startup.address.url + '/auth/callback?code=' + startup.accessCode;

      }
    };

    return <StartupSelectBox {...startupSelectBoxAttributes} />;
  }

  function renderStartupsQuickAccess() {
    return renderStartupSelectBox();
  }

  function renderStartupNavigation() {

    const navigationProps = {
      startup,
      onOptionClick: props.onOptionClick
    };

    return <StartupNavigation {...navigationProps} />;
  }

  function renderLoginButton() {

    if (application.isAuthenticated() === true) {
      return;
    }

    const authenticationSettings = props.startup.authentication;

    let buttonText = 'Login or Signup';

    if (authenticationSettings.enabled === true) {
      buttonText = authenticationSettings.buttonText || `Login using ${props.startup.name}`;
    }

    if (application.fullscreenToggle.isExpanded() === true) {
      buttonText = 'L/S';
    }

    const loginButtonAttributes = {
      id: 'login-button',
      className: 'startup-themed-button startup-button-color startup-text-color',
      onClick() {
        handleLoginButtonClick();
      }
    };

    return <div id="login-button-holder">
      <a {...loginButtonAttributes}>{buttonText}</a>
    </div>;

  }

  function renderAuthenticationQuickAccess() {
    return renderLoginButton();
  }

  function renderEditStartupDialog() {

    if (startup === null) {
      return;
    }

    if (rootState.displayEditStartupDialog === false) {
      return;
    }

    const dialogProperties = {
      stackTopClass: 'quick-access-elevated',
      contentHolderClassName: 'startup-form-container',
      cancelOnEscape: true,
      onClose() {
        handleStartupEditingCompleted();
      }
    };

    const startupFormProperties = {
      startupId: startup._id,
      mode: 'EDIT',
      defaultActiveSection: rootState.activeStartupFormSection,
      async onComplete() {
        handleStartupEditingCompleted();
      },
      onClose() {
        handleStartupEditingCompleted();
      }
    };

    return <SimpleDialog {...dialogProperties}>
      <Suspense fallback={<ht-spinner size="small" />}>
        <StartupForm {...startupFormProperties} />
      </Suspense>
    </SimpleDialog>;

  }

  function renderUserActionsQuickAccess() {

    const userAccessAttributes = {
      id: 'user-quick-access-main',
      ref: userQuickAccessRef
    };

    return <div {...userAccessAttributes}>

      <div id="user-profile-control-container">
        {renderProfileSettingsLink()}
      </div>

    </div>;

  }

  function renderUnauthenticatedQuickAccessContent() {
    return <TagsNavigator />;
  }

  function tryRenderStartupNavigationForCustomer() {

    if (application.isAuthenticated() === false) {
      return;
    }

    if (application.menuList.length === 0) {
      return;
    }

    return <div id="startup-navigation-content">
      {renderStartupNavigation()}
    </div>;

  }

  function renderCustomerQuickAccessContent() {

    return <React.Fragment>
      {renderUnauthenticatedQuickAccessContent()}
      {tryRenderStartupNavigationForCustomer()}
    </React.Fragment>;

  }

  function renderUserActionsOrAuthenticationQuickAccess() {

    if (application.isAuthenticated() === false) {
      return renderAuthenticationQuickAccess();
    }

    return renderUserActionsQuickAccess();

  }

  function renderSettingControlContent() {

    return <React.Fragment>
      <SettingsIcon />
      <span className="navigation-link-name">Settings</span>
    </React.Fragment>;

  }

  function renderStartupSettingControl() {

    if (application.accessController.isAdministrator() === false) {
      return;
    }

    const settingControlAttributes = {
      className: 'setting-navigation-link-control',
      async onClick() {

        const getSectionNameForTabTitleFn = await loadStartupFormHelpers();
        const sectionName = getSectionNameForTabTitleFn('About');

        setSearchParameters(function updateSearchParameters(searchParameters) {

          searchParameters.set('settings', sectionName);

          return searchParameters;

        });
      }
    };

    return <button {...settingControlAttributes}>
      {renderSettingControlContent()}
    </button>;
  }

  function renderAssistantUnreadMessagesCueElement(menuItemName) {

    if (rootState.displayAssistantUnreadMessageCue === false) {
      return;
    }

    return <div className="navigation-cue"></div>;

  }

  function renderStartupAssistant() {

    const assistantControlAttributes = {
      className: 'assistant-navigation-link-control',
      onClick() {}
    };

    return <div {...assistantControlAttributes}>
      {renderAssistantUnreadMessagesCueElement()}
      <AssistantIcon className="ask-ai-icon" />
      <span className="navigation-link-name">Assistant</span>
    </div>;
  }

  function renderStartupSearchControl() {
    return;
  }

  function renderQuickAccessContent() {

    if (application.isAuthenticated() === false) {
      return renderUnauthenticatedQuickAccessContent();
    }

    if (application.accessController.isCustomer() === true) {
      return renderCustomerQuickAccessContent();
    }

    return <div id="authenticated-quick-access-content" className="hide-scrollbar" ref={authenticatedContentRef}>

      <div id="settings-and-search-content">
        {renderStartupSettingControl()}

        {renderStartupSearchControl()}
      </div>

      <div id="startup-navigation-content">
        {renderStartupNavigation()}
      </div>

    </div>;
  }

  function renderExpandButton() {

    const expandButtonAttributes = {
      id: 'right-panel-width-toggle-button',
      className: 'expand-button',
      onClick() {
        toggleExpandedState();
      }
    };

    return <button {...expandButtonAttributes}>
      <RestoreApplicationWidthIcon />
    </button>;

  }

  function tryRenderSpinnerDialog() {

    if (rootState.displaySpinnerDialog === false) {
      return;
    }

    const dialogProperties = {
      stackTopClass: 'quick-access-elevated',
      contentHolderClassName: 'spinner-dialog-container',
      onClose() {}
    };

    return <SimpleDialog {...dialogProperties}>
      <ht-spinner size="small" />
    </SimpleDialog>;

  }

  return <div id="quick-access-main" className="top-level-section-content">

    <div id="quick-access-main-content">
      <div id="startups-quick-access">
        {renderStartupsQuickAccess()}
      </div>

      <div id="quick-access-content">
        {renderQuickAccessContent()}
      </div>
      {tryRenderOnboardingLink()}
      <div id="user-actions-and-authentication-quick-access">
        {renderUserActionsOrAuthenticationQuickAccess()}
      </div>
    </div>

    <div id="quick-access-secondary-content">
      {renderAuthenticationPage()}
      {renderEditStartupDialog()}
      {tryRenderSpinnerDialog()}
    </div>

  </div>;

}

export default QuickAccess;